import { useState, useEffect } from 'react';
import {
  Menu,
  Button,
  MenuButton,
  MenuList,
  MenuItem,
  HStack,
  Avatar,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { ScrapesiteModel } from 'models/scrapejob/ScrapesiteModel';

export interface ScrapesiteDropdownProps {
  scrapesites: ScrapesiteModel[];
  onScrapesiteSelect?: (scrapesites: ScrapesiteModel[]) => void;
  multiSelectable?: boolean;
  defaultSelectedScrapesites?: ScrapesiteModel[];
  maxWidth?: boolean;
}

export default function ScrapesiteDropdown({
  scrapesites,
  onScrapesiteSelect,
  defaultSelectedScrapesites = [],
  multiSelectable = false,
  maxWidth = false,
}: ScrapesiteDropdownProps) {
  const [selectedScrapesites, setSelectedScrapesites] = useState<ScrapesiteModel[]>(defaultSelectedScrapesites);
  const highlightBg = useColorModeValue('blue.50', 'blue.900');
  const highlightBorder = useColorModeValue('blue.500', 'blue.300');

  useEffect(() => {
    setSelectedScrapesites(defaultSelectedScrapesites);
  }, [defaultSelectedScrapesites]);

  const handleScrapesiteClick = (scrapesite: ScrapesiteModel) => {
    let newSelectedScrapesites: ScrapesiteModel[];

    if (multiSelectable) {
      const isSelected = selectedScrapesites.some(site => site.id === scrapesite.id);
      if (isSelected) {
        newSelectedScrapesites = selectedScrapesites.filter(site => site.id !== scrapesite.id);
      } else {
        newSelectedScrapesites = [...selectedScrapesites, scrapesite];
      }
    } else {
      newSelectedScrapesites = [scrapesite];
    }

    setSelectedScrapesites(newSelectedScrapesites);
    onScrapesiteSelect?.(newSelectedScrapesites);
  };

  const isScrapesiteSelected = (scrapesiteId: string): boolean => {
    return selectedScrapesites.some(site => site.id === scrapesiteId);
  };

  const renderMenuButton = () => {
    if (multiSelectable) {
      if (selectedScrapesites.length > 0) {
        return (
          <Text fontSize="sm" color="gray.300">
            {`${selectedScrapesites.length} Sites Selected`}
          </Text>
        );
      }
      return <Text fontSize="sm" color="gray.300">Select Sites</Text>;
    }

    if (selectedScrapesites.length > 0) {
      const site = selectedScrapesites[0];
      return (
        <HStack spacing={3}>
          <Avatar size="sm" name={site.name} src={site.image || undefined} />
          <Text fontSize="sm">{site.name}</Text>
        </HStack>
      );
    }

    return <Text fontSize="sm" color="gray.300">Select A Site</Text>;
  };

  return (
    <Menu closeOnSelect={!multiSelectable}>
      <MenuButton
        borderRadius="8px"
        variant="outline"
        textAlign="left"
        width={maxWidth ? '100%' : 'auto'}
        as={Button}
        rightIcon={<ChevronDownIcon />}
      >
        {renderMenuButton()}
      </MenuButton>
      <MenuList width="100%" maxHeight="300px" overflowY="auto" backgroundColor="white" zIndex="100">
        {scrapesites.map((site, index) => {
          const isSelected = isScrapesiteSelected(site.id);
          return (
            <MenuItem
              key={`scrapesite_dropdown_${index}`}
              px={3}
              py={2}
              onClick={() => handleScrapesiteClick(site)}
              backgroundColor={isSelected ? highlightBg : 'transparent'}
              borderLeft={isSelected ? '3px solid' : '3px solid transparent'}
              borderColor={isSelected ? highlightBorder : 'transparent'}
              _hover={{ backgroundColor: isSelected ? highlightBg : 'gray.100' }}
            >
              <HStack spacing={3}>
                <Avatar size="sm" name={site.name} src={site.image || undefined} />
                <Text fontSize="sm">{site.name}</Text>
              </HStack>
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
}
